import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
    assignImageToGallery,
    createGalleryBlock,
    deleteGalleryBlock,
    deleteGalleryBlockVideo,
    fetchAllGalleriesAdmin,
    regenerateAiTextsForGallery,
    updateGalleryBlock,
    uploadGalleryBlockVideo,
} from '../services/api';
import { Gallery, GalleryBlock, Image } from '../types';

type RouteParams = {
    id: string;
};

const AdminGallery: React.FC = () => {
    const { id } = useParams<RouteParams>();
    const [gallery, setGallery] = useState<Gallery | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [regenerating, setRegenerating] = useState(false);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [isBlockFormOpen, setIsBlockFormOpen] = useState(false);
    const [editingBlock, setEditingBlock] = useState<GalleryBlock | null>(null);
    const [blockTitle, setBlockTitle] = useState('');
    const [blockDescription, setBlockDescription] = useState('');
    const [blockVisible, setBlockVisible] = useState(true);
    const [blockSortOrder, setBlockSortOrder] = useState(100);
    const [blockImageIds, setBlockImageIds] = useState<number[]>([]);
    const [blockVideoFile, setBlockVideoFile] = useState<File | null>(null);
    const [removeBlockVideo, setRemoveBlockVideo] = useState(false);

    const loadGallery = async () => {
        try {
            const all = await fetchAllGalleriesAdmin();
            const found = all.find((g: Gallery) => String(g.id) === String(id));
            if (!found) {
                setError('Galleri ikke fundet');
            } else {
                setGallery(found);
            }
        } catch (e) {
            setError('Kunne ikke hente galleriet');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadGallery();
    }, [id]);

    const images: Image[] = useMemo(() => {
        return gallery?.images || [];
    }, [gallery]);

    const blocks: GalleryBlock[] = useMemo(() => {
        return [...(gallery?.blocks || [])].sort((a, b) => {
            const orderDiff = Number(a.sortOrder) - Number(b.sortOrder);
            if (orderDiff !== 0) return orderDiff;
            return Number(a.id) - Number(b.id);
        });
    }, [gallery?.blocks]);

    const toggleBlockImage = (imageId: number, checked: boolean, currentIds: number[]) => {
        if (checked) {
            return Array.from(new Set([...currentIds, imageId]));
        }
        return currentIds.filter((currentId) => currentId !== imageId);
    };

    const resetBlockForm = () => {
        setBlockTitle('');
        setBlockDescription('');
        setBlockVisible(true);
        setBlockSortOrder(100);
        setBlockImageIds([]);
        setBlockVideoFile(null);
        setRemoveBlockVideo(false);
        setEditingBlock(null);
    };

    const openCreateBlockForm = () => {
        resetBlockForm();
        setIsBlockFormOpen(true);
    };

    const openEditBlockForm = (block: GalleryBlock) => {
        setEditingBlock(block);
        setBlockTitle(block.title || '');
        setBlockDescription(block.description || '');
        setBlockVisible(Boolean(block.isVisible));
        setBlockSortOrder(Number.isFinite(Number(block.sortOrder)) ? Number(block.sortOrder) : 100);
        setBlockImageIds(
            Array.isArray(block.imageIds)
                ? block.imageIds.map((imageId) => Number(imageId)).filter((imageId) => Number.isFinite(imageId))
                : (block.images || []).map((image) => Number(image.id)).filter((imageId) => Number.isFinite(imageId))
        );
        setBlockVideoFile(null);
        setRemoveBlockVideo(false);
        setIsBlockFormOpen(true);
    };

    const handleSaveBlock = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!gallery) return;

        setError(null);
        setStatusMessage(null);
        try {
            const payload = {
                title: blockTitle,
                description: blockDescription,
                isVisible: blockVisible,
                sortOrder: blockSortOrder,
                imageIds: blockImageIds,
            };
            const savedBlock = editingBlock
                ? await updateGalleryBlock(gallery.id, editingBlock.id, payload)
                : await createGalleryBlock(gallery.id, payload);

            const savedBlockId = editingBlock?.id || savedBlock?.id;
            if (savedBlockId && removeBlockVideo && !blockVideoFile) {
                await deleteGalleryBlockVideo(gallery.id, savedBlockId);
            }
            if (savedBlockId && blockVideoFile) {
                await uploadGalleryBlockVideo(gallery.id, savedBlockId, blockVideoFile);
            }

            resetBlockForm();
            setIsBlockFormOpen(false);
            setStatusMessage('Galleriblok gemt.');
            await loadGallery();
        } catch (e) {
            setError('Kunne ikke gemme galleriblokken');
        }
    };

    const handleDeleteBlock = async (block: GalleryBlock) => {
        if (!gallery) return;
        if (!window.confirm('Vil du slette denne galleriblok?')) return;
        try {
            await deleteGalleryBlock(gallery.id, block.id);
            await loadGallery();
        } catch (e) {
            setError('Kunne ikke slette galleriblokken');
        }
    };

    const handleRegenerateAll = async () => {
        if (!gallery) return;
        setRegenerating(true);
        setError(null);
        setStatusMessage(null);
        try {
            const result = await regenerateAiTextsForGallery(gallery.id);
            const count = result?.count ?? 0;
            const failed = result?.failed ?? 0;
            const incomplete = result?.incomplete?.length ?? 0;
            setStatusMessage(
                failed || incomplete
                    ? `AI-tekster opdateret: ${count} ok, ${failed} fejlede, ${incomplete} med manglende felter.`
                    : `AI-tekster opdateret for ${count} billeder.`
            );
            await loadGallery();
        } catch (e) {
            // @ts-ignore
            const message = e && e.response && e.response.data && e.response.data.message;
            setError(message || 'Kunne ikke regenerere AI-tekster');
        } finally {
            setRegenerating(false);
        }
    };

    const handleRemoveFromGallery = async (imageId: string) => {
        if (!gallery) return;
        if (!window.confirm('Vil du fjerne billedet fra galleriet?')) return;
        try {
            await assignImageToGallery(imageId, null);
            await loadGallery();
        } catch (e) {
            setError('Kunne ikke fjerne billedet fra galleriet');
        }
    };

    if (loading) return <div>Indlæser...</div>;
    if (!gallery) return <div>{error || 'Galleri ikke fundet'}</div>;

    return (
        <div className="admin-image-edit">
            <div className="admin-image-edit__header">
                <div>
                    <h1>{gallery.title}</h1>
                    <p className="gallery-list-meta">
                        {gallery.description || '—'} · {images.length} billeder
                        {gallery.isPublic ? ' · Offentlig' : ' · Beskyttet'}
                        {gallery.isActive === false ? ' · Inaktiv' : ' · Aktiv'}
                    </p>
                </div>
                <div className="admin-actions">
                    <Link className="link-button" to="/admin#galleries">
                        Tilbage
                    </Link>
                    <button type="button" onClick={handleRegenerateAll} disabled={regenerating}>
                        {regenerating ? (
                            <span className="spinner" aria-label="Regenererer" />
                        ) : (
                            'Regenerer alle AI-tekster'
                        )}
                    </button>
                </div>
            </div>

            {error && <div className="error">{error}</div>}
            {statusMessage && <div className="gallery-list-meta">{statusMessage}</div>}

            <section className="admin-section admin-gallery-blocks">
                <div className="admin-home-block-item__head">
                    <div>
                        <h2>Galleriblokke</h2>
                        <p className="gallery-list-meta">Tekstblokke, video og udvalgte billeder til dette galleri.</p>
                    </div>
                    <button type="button" onClick={openCreateBlockForm}>Opret blok</button>
                </div>

                {isBlockFormOpen && (
                    <form onSubmit={handleSaveBlock} className="modal-form admin-home-block-form admin-gallery-block-form">
                        <div className="admin-home-block-form__main">
                            <label>
                                Titel
                                <input
                                    type="text"
                                    value={blockTitle}
                                    onChange={(event) => setBlockTitle(event.target.value)}
                                    required
                                />
                            </label>
                            <label>
                                Tekst
                                <textarea
                                    value={blockDescription}
                                    onChange={(event) => setBlockDescription(event.target.value)}
                                />
                            </label>
                            <label>
                                Sortering
                                <input
                                    type="number"
                                    value={blockSortOrder}
                                    onChange={(event) => setBlockSortOrder(Number(event.target.value) || 0)}
                                />
                            </label>
                            <label className="checkbox-row">
                                <input
                                    type="checkbox"
                                    checked={blockVisible}
                                    onChange={(event) => setBlockVisible(event.target.checked)}
                                />
                                Vis blok
                            </label>
                            <div className="admin-home-block-video">
                                {editingBlock?.videoUrl && !removeBlockVideo ? (
                                    <video className="admin-home-block-video__preview" src={editingBlock.videoUrl} controls preload="metadata" />
                                ) : null}
                                <label>
                                    Video
                                    <input
                                        type="file"
                                        accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
                                        onChange={(event) => {
                                            setBlockVideoFile(event.target.files?.[0] || null);
                                            setRemoveBlockVideo(false);
                                        }}
                                    />
                                </label>
                                {blockVideoFile ? <p className="gallery-list-meta">Ny video: {blockVideoFile.name}</p> : null}
                                {editingBlock?.videoUrl ? (
                                    <label className="checkbox-row">
                                        <input
                                            type="checkbox"
                                            checked={removeBlockVideo}
                                            onChange={(event) => {
                                                setRemoveBlockVideo(event.target.checked);
                                                if (event.target.checked) {
                                                    setBlockVideoFile(null);
                                                }
                                            }}
                                        />
                                        Fjern eksisterende video
                                    </label>
                                ) : null}
                            </div>
                        </div>
                        <div className="admin-home-block-images">
                            <p className="gallery-list-meta">Vælg billeder fra galleriet</p>
                            <div className="admin-home-block-images__grid">
                                {images.map((image) => (
                                    <label key={`gallery-block-${image.id}`} className="checkbox-row">
                                        <input
                                            type="checkbox"
                                            checked={blockImageIds.includes(Number(image.id))}
                                            onChange={(event) =>
                                                setBlockImageIds((current) =>
                                                    toggleBlockImage(Number(image.id), event.target.checked, current)
                                                )
                                            }
                                        />
                                        <span>{image.title || image.filename || `Billede ${image.id}`}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="modal-actions admin-home-block-form__actions">
                            <button
                                type="button"
                                onClick={() => {
                                    resetBlockForm();
                                    setIsBlockFormOpen(false);
                                }}
                            >
                                Annuller
                            </button>
                            <button type="submit">{editingBlock ? 'Gem' : 'Opret'}</button>
                        </div>
                    </form>
                )}

                <ul className="admin-home-block-list">
                    {blocks.map((block) => (
                        <li key={block.id} className="admin-home-block-item admin-home-block-item--compact">
                            <div className="admin-home-block-item__head">
                                <strong>{block.title}</strong>
                                <span className="admin-home-block-item__order">#{block.sortOrder}</span>
                            </div>
                            <div className="admin-home-block-item__meta">
                                <span className="admin-row__meta">Synlig: {block.isVisible ? 'Ja' : 'Nej'}</span>
                                <span className="admin-home-block-item__sep" aria-hidden="true">•</span>
                                <span className="admin-row__meta">Billeder: {(block.imageIds || block.images || []).length}</span>
                                <span className="admin-home-block-item__sep" aria-hidden="true">•</span>
                                <span className="admin-row__meta">Video: {block.videoUrl ? 'Ja' : 'Nej'}</span>
                            </div>
                            {block.description ? <p className="gallery-list-meta">{block.description}</p> : null}
                            <div className="admin-list-actions">
                                <button type="button" className="link-button" onClick={() => openEditBlockForm(block)}>
                                    Rediger
                                </button>
                                <button type="button" className="link-button link-button--danger" onClick={() => handleDeleteBlock(block)}>
                                    Slet
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            </section>

            <ul className="admin-image-list">
                {images.map((img) => (
                    <li key={img.id} className="admin-image-item">
                        <Link
                            to={`/admin/images/${img.id}?returnTo=${encodeURIComponent(`/admin/galleries/${gallery.id}`)}`}
                            aria-label={`Rediger ${img.title || 'billede'}`}
                        >
                            <img src={img.thumbnailUrl} alt={img.title || 'billede'} />
                        </Link>
                        <div className="admin-row admin-row--image">
                            <Link
                                className="admin-row__title"
                                to={`/admin/images/${img.id}?returnTo=${encodeURIComponent(`/admin/galleries/${gallery.id}`)}`}
                            >
                                {img.title || 'Uden titel'}
                            </Link>
                            <span className="admin-row__meta admin-row__truncate">
                                {img.description || '—'}
                            </span>
                            <span className="admin-row__meta admin-row__truncate">
                                {img.filename ? `Fil: ${img.filename}` : 'Fil: —'}
                            </span>
                            <span className="admin-row__meta admin-row__truncate">
                                {gallery.title}
                            </span>
                            <span className="admin-row__meta">
                                Forside: {img.showOnHomepage ? 'Ja' : 'Nej'}
                            </span>
                            <span className="admin-row__meta">
                                Oprettet {new Date(img.createdAt).toLocaleDateString('da-DK')}
                            </span>
                            <div className="admin-list-actions">
                                <Link className="link-button" to={`/admin/images/${img.id}`}>
                                    Rediger
                                </Link>
                                <button
                                    type="button"
                                    className="link-button"
                                    onClick={() => handleRemoveFromGallery(img.id)}
                                >
                                    Fjern fra galleri
                                </button>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default AdminGallery;
